import PropTypes from "prop-types";
import { useState } from "react";
import { Box } from "@mui/material";
import { IconWorld } from "@tabler/icons";

/**
 * A country flag.
 *
 * The `emoji` column on `nationality` and `country` does not hold an emoji —
 * it holds a two-letter ISO code ("fr", "dz"). Rendering it as text prints
 * the code, which is what the country cards, the profile header and the
 * wizard's nationality picker were all doing.
 *
 * Images come from flagcdn, matching what the country edit form already uses
 * (see DynamicModal's dropdownemoji branch), so flags look the same wherever
 * they appear.
 *
 * Anything that is not a two-letter code is rendered as-is: a few rows may
 * genuinely hold an emoji character, and those should still show.
 */

// flagcdn publishes fixed sizes; these are the three we use, with a 2x source
// for retina. Picking from its real sizes avoids browser upscaling.
const SIZES = {
  sm: { w: 20, h: 15, w2: 40, h2: 30 },
  md: { w: 24, h: 18, w2: 48, h2: 36 },
  lg: { w: 32, h: 24, w2: 64, h2: 48 },
};

const Flag = ({ code, size = "sm", title, sx = {} }) => {
  const [failed, setFailed] = useState(false);
  const raw = String(code ?? "").trim();
  const isIso = /^[A-Za-z]{2}$/.test(raw);
  const { w, h, w2, h2 } = SIZES[size] ?? SIZES.sm;

  // No flag set for this country: a neutral globe the size of a flag, so
  // the row lines up with its neighbours and the gap reads as missing data
  // (fixed by picking the country in Edit details), not as a broken image.
  if (!raw) {
    return (
      <Box
        component="span"
        role={title ? "img" : undefined}
        aria-label={title ? `${title}: no flag set` : undefined}
        aria-hidden={title ? undefined : true}
        title="No flag set"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: w,
          height: h,
          borderRadius: "2px",
          bgcolor: "action.selected",
          color: "text.secondary",
          boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
          ...sx,
        }}
      >
        <IconWorld size={h - 4} stroke={1.75} aria-hidden />
      </Box>
    );
  }

  // Not an ISO code (already a flag character), or the image would not load.
  // Falling back to the code itself keeps the row readable rather than
  // showing a broken-image icon on every card.
  if (!isIso || failed) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          minWidth: w,
          height: h,
          fontSize: h * 0.62,
          lineHeight: 1,
          ...(isIso
            ? {
                px: 0.4,
                borderRadius: "2px",
                textTransform: "uppercase",
                letterSpacing: ".04em",
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
              }
            : {}),
          ...sx,
        }}
      >
        {raw}
      </Box>
    );
  }

  const iso = raw.toLowerCase();

  return (
    <Box
      component="img"
      src={`https://flagcdn.com/${w}x${h}/${iso}.png`}
      srcSet={`https://flagcdn.com/${w2}x${h2}/${iso}.png 2x`}
      width={w}
      height={h}
      alt={title ?? iso.toUpperCase()}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        display: "block",
        flexShrink: 0,
        borderRadius: "2px",
        // A white flag on a white card would otherwise have no edge.
        boxShadow: "0 0 0 1px rgba(0,0,0,.12)",
        ...sx,
      }}
    />
  );
};

Flag.propTypes = {
  /** Two-letter ISO code, or an emoji character. */
  code: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  title: PropTypes.string,
  sx: PropTypes.object,
};

export default Flag;
